//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { ReactElement, useEffect, useState } from "react";

// Material ui Imports
import { Drawer, SvgIconProps, SxProps, useTheme } from "@mui/material";
import Rotate90DegreesCwRoundedIcon from "@mui/icons-material/Rotate90DegreesCwRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import AudiotrackRoundedIcon from "@mui/icons-material/AudiotrackRounded";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import TemplateIcon from "@mui/icons-material/FlipToFrontRounded";
import Model3DIcon from "@mui/icons-material/ViewInArRounded";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import { CalendarMonthRounded, PersonRounded, GroupRounded } from "@mui/icons-material";

// Custom components imports (and hooks and helpers)
import VTCreatorTabOption from "./VTCreatorTabOption";
import { EditHotSpotDialogTab } from "../../models/current.models";
import { useAppSelector } from "../../store/hooks";

// Media imports
import Icon360Image from "../../assets/icons/Icon360Image";
import Icon360Video from "../../assets/icons/Icon360Video";
import { EOrganisation } from "../../models/organisations.model";
import { setCurrentRichTextEditorMode } from "../../store/slices/current.slice";
import IconImmersiveMedia from "../../assets/icons/IconImmersiveMedia";
import IconImmersiveImage from "../../assets/icons/IconImmersiveImage";
import IconImmersiveVideo from "../../assets/icons/IconImmersiveVideo";
import { useLocation } from "react-router-dom";

//---------------------------------------------------------------------------------------------------
// ## MUI STYLED ##
// Layout stuff remains inline, other styling pulled into it's own components

//---------------------------------------------------------------------------------------------------
// ## INTERFACES ##

// Tab options

interface IProps {
    setCloseDraw: () => void;
    setOpenDraw: any;
    disabled?: string[];
    sx?: SxProps;
    isEditHotSpotDialogOpen: boolean;
    currentEditHotSpotDialogTabOpen: EditHotSpotDialogTab | undefined;
}

// Props to map the tab options
interface ICreatorTabsProps {
    tabName: string;
    icon: ReactElement<SvgIconProps>;
}

function shouldDisableTabWhenEditHotspotIsOpen(
    tabName: string,
    isEditHotSpotDialogOpen: boolean,
    editHotspotDialogTabOpen: EditHotSpotDialogTab | undefined,
    richTextEditorMode: "presentation mode" | "normal mode" | undefined,
    // @ts-ignore
): boolean {
    if (isEditHotSpotDialogOpen) {
        if (!["Images", "Videos", "Audio"].includes(tabName) && editHotspotDialogTabOpen === "actions") {
            return true;
        } else if (
            !["Images", "Videos", "Audio"].includes(tabName) &&
            editHotspotDialogTabOpen === "content" &&
            richTextEditorMode === "normal mode"
        ) {
            return true;
        } else if (
            !["Images", "Text"].includes(tabName) &&
            editHotspotDialogTabOpen === "content" &&
            richTextEditorMode === "presentation mode"
        ) {
            return true;
        } else if (editHotspotDialogTabOpen === "style") {
            return true;
        } else if (editHotspotDialogTabOpen === "preview") {
            return true;
        }
    } else {
        return false;
    }
}

export const getIsOnEditMarksPage = (routePathName: string) => !!routePathName.match(/.*Edit/);

//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Only display layouts in pages
export default function VTCreatorTabs({
    isEditHotSpotDialogOpen,
    currentEditHotSpotDialogTabOpen,
    setCloseDraw,
    setOpenDraw,
    disabled,
    sx,
}: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## HOOKS ##
    const theme = useTheme();

    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##
    const { organisation } = useAppSelector((state) => state.userReducer);
    const { currentSidePanel, currentRichTextEditorMode, selectedAttempt, selectedUser } = useAppSelector(
        (state) => state.currentReducer,
    );
    const { mediaUploading } = useAppSelector((state) => state.mediaUploadReducer);

    const location = useLocation();

    const isOnEditMarksPage = getIsOnEditMarksPage(location.pathname);

    // ## USE EFFECTS ##

    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##
    const [tabOptions, setTabOptions] = useState<ICreatorTabsProps[]>([
        {
            tabName: "Version",
            icon: <PersonRounded />,
        },
        {
            tabName: "Users",
            icon: <PersonRounded />,
        },
        {
            tabName: "Attempts",
            icon: <PersonRounded />,
        },
    ]);

    //-------------------------------------------------------------------------------------------------
    // ## USE EFFECTS ##

    //-------------------------------------------------------------------------------------------------
    // ## CUSTOM FUNCTIONS ##
    // Render for the tab buttons

    //-------------------------------------------------------------------------------------------------
    // ## RETURN ##
    // Show creator page tabs options
    return (
        <Drawer
            variant="permanent"
            anchor="left"
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.background.default,
                    position: "absolute",
                    top: "68px",
                    width: "80px",
                    zIndex: 1450,
                    height: "calc(100vh - 67px)",
                    ...sx,
                },
            }}
        >
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Templates Options */}
            {tabOptions
                // .filter((element: ICreatorTabsProps) =>
                //     disabled && disabled.length ? !disabled.includes(element.tabName) : element.tabName,
                // )
                .map((element: ICreatorTabsProps) => (
                    <VTCreatorTabOption
                        currentTab={currentSidePanel.currentTab}
                        tabName={element.tabName}
                        icon={element.icon}
                        setCloseDraw={() => !mediaUploading && setCloseDraw()}
                        setOpenDraw={() => !mediaUploading && setOpenDraw(element.tabName)}
                        disable={(element.tabName === "Attempts" && selectedUser === undefined) || isOnEditMarksPage}
                    />
                ))}
        </Drawer>
    );
}
